import type {
  ActionCandidate,
  ActionGroupCandidate,
} from "../../../types/candidate";

// eslint-disable-next-line import/no-unused-modules
export type GroupLocator =
  | { kind: "root"; instanceId: number }
  | { kind: "loop"; instanceId: number }
  | { kind: "branch"; instanceId: number; branchHash: string | "else" };

/**
 * Depth-first walk over action groups, including groups nested in loops,
 * repeats, and branches/else-branches. The single place that knows the
 * group-tree shape: when a new group kind is added, extend the recursion
 * HERE and every consumer below inherits it.
 */
export const walkActionGroups = (
  groups: ActionGroupCandidate[],
  visit: (group: ActionGroupCandidate, parent?: ActionGroupCandidate) => void,
  parent?: ActionGroupCandidate,
): void => {
  groups.forEach((group) => {
    visit(group, parent);
    if (group.config.oneofKind === "loop") {
      walkActionGroups(group.config.loop.actionGroups, visit, group);
    }
    if (group.config.oneofKind === "branches") {
      group.config.branches.branches.forEach((branch) => {
        walkActionGroups(branch.actionGroups, visit, group);
      });
      if (group.config.branches.elseBranch !== undefined) {
        walkActionGroups(
          group.config.branches.elseBranch.actionGroups,
          visit,
          group,
        );
      }
    }
    if (group.config.oneofKind === "repeat") {
      // RepeatActionGroupConfig.actionGroups is generated as ActionGroupConfig[] (no hash/description),
      // not ActionGroupCandidate. The cast is safe: repeat isn't constructible via UI, and the walker
      // only reads structural fields. Note: mapActionsDeep skips repeat (hash assignment skips contents).
      walkActionGroups(
        group.config.repeat.actionGroups as unknown as ActionGroupCandidate[],
        visit,
        group,
      );
    }
  });
};

export const collectActionsDeep = (
  groups: ActionGroupCandidate[],
): ActionCandidate[] => {
  const result: ActionCandidate[] = [];
  walkActionGroups(groups, (group) => {
    if (group.config.oneofKind === "actions") {
      result.push(...group.config.actions.actions);
    }
  });
  return result;
};

/**
 * Steps that count toward maxActionsPerAutomation: every leaf action plus
 * every Conditional Logic ("branches") and Looping ("loop") block itself —
 * per spec the blocks consume a slot of the per-automation limit alongside
 * the actions inside them. Repeat groups aren't UI-constructible and only
 * their actions are counted.
 */
export const countAutomationStepsDeep = (
  groups: ActionGroupCandidate[],
): number => {
  let count = 0;
  walkActionGroups(groups, (group) => {
    if (group.config.oneofKind === "actions") {
      count += group.config.actions.actions.length;
    }
    if (
      group.config.oneofKind === "loop" ||
      group.config.oneofKind === "branches"
    ) {
      count += 1;
    }
  });
  return count;
};

const mapActionsWithin = (
  groups: ActionGroupCandidate[],
  fn: (action: ActionCandidate) => ActionCandidate,
  descendIntoLoops: boolean,
): ActionGroupCandidate[] =>
  groups.map((group) => {
    if (group.config.oneofKind === "actions") {
      return {
        ...group,
        config: {
          ...group.config,
          actions: { actions: group.config.actions.actions.map(fn) },
        },
      };
    }
    if (group.config.oneofKind === "loop") {
      if (!descendIntoLoops) return group;
      return {
        ...group,
        config: {
          ...group.config,
          loop: {
            ...group.config.loop,
            actionGroups: mapActionsWithin(
              group.config.loop.actionGroups,
              fn,
              descendIntoLoops,
            ),
          },
        },
      };
    }
    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      return {
        ...group,
        config: {
          ...group.config,
          branches: {
            ...branches,
            branches: branches.branches.map((branch) => ({
              ...branch,
              actionGroups: mapActionsWithin(
                branch.actionGroups,
                fn,
                descendIntoLoops,
              ),
            })),
            elseBranch:
              branches.elseBranch === undefined
                ? undefined
                : {
                    ...branches.elseBranch,
                    actionGroups: mapActionsWithin(
                      branches.elseBranch.actionGroups,
                      fn,
                      descendIntoLoops,
                    ),
                  },
          },
        },
      };
    }
    return group;
  });

export const mapActionsDeep = (
  groups: ActionGroupCandidate[],
  fn: (action: ActionCandidate) => ActionCandidate,
): ActionGroupCandidate[] => mapActionsWithin(groups, fn, true);

/**
 * Maps every action that belongs to the given groups' OWN loop scope: the
 * direct "actions" groups and, at any depth, the branches/else-branches
 * nested inside them -- but not the actions of a nested loop, which owns its
 * own Current Item scope and is returned as is. Pure/immutable like
 * `mapActionsDeep`. Pair it with a loop's `actionGroups` to touch exactly the
 * actions that may reference that loop's Current Item.
 */
export const mapScopeActionsDeep = (
  groups: ActionGroupCandidate[],
  fn: (action: ActionCandidate) => ActionCandidate,
): ActionGroupCandidate[] => mapActionsWithin(groups, fn, false);

/**
 * Strips every direct "actions" group whose action list is empty, at every
 * level of the tree (the given list, loop bodies, and each branch/else-branch
 * body). Pure/immutable, same style as `mapActionsDeep`: non-empty "actions"
 * groups and container groups (loop/branches) are preserved, containers are
 * rebuilt with their filtered children, and the input is never mutated.
 *
 * This exists because the backend engine rejects an "actions" group with zero
 * actions (`Actions group must contain at least one action`), while the UI
 * model requires every container to always keep a direct "actions" group as
 * its write/render target (see `ensureDirectActionsGroups`, its inverse, for
 * the read side). Calling this right before a container's own contents are
 * serialized for save keeps the two requirements from conflicting: the UI
 * keeps its always-present direct group, the wire payload only carries it
 * when it's non-empty.
 */
export const stripEmptyActionsGroups = (
  groups: ActionGroupCandidate[],
): ActionGroupCandidate[] =>
  groups
    .filter(
      (group) =>
        !(
          group.config.oneofKind === "actions" &&
          group.config.actions.actions.length === 0
        ),
    )
    .map((group) => {
      if (group.config.oneofKind === "loop") {
        return {
          ...group,
          config: {
            ...group.config,
            loop: {
              ...group.config.loop,
              actionGroups: stripEmptyActionsGroups(
                group.config.loop.actionGroups,
              ),
            },
          },
        };
      }
      if (group.config.oneofKind === "branches") {
        const { branches } = group.config;
        return {
          ...group,
          config: {
            ...group.config,
            branches: {
              ...branches,
              branches: branches.branches.map((branch) => ({
                ...branch,
                actionGroups: stripEmptyActionsGroups(branch.actionGroups),
              })),
              elseBranch:
                branches.elseBranch === undefined
                  ? undefined
                  : {
                      ...branches.elseBranch,
                      actionGroups: stripEmptyActionsGroups(
                        branches.elseBranch.actionGroups,
                      ),
                    },
            },
          },
        };
      }
      return group;
    });

export const collectInstanceIdsDeep = (
  groups: ActionGroupCandidate[],
): number[] => {
  const ids: number[] = [];
  walkActionGroups(groups, (group) => {
    ids.push(group.instanceId);
    if (group.config.oneofKind === "actions") {
      group.config.actions.actions.forEach((action) => {
        if (action.actionReference?.instanceId !== undefined) {
          ids.push(action.actionReference.instanceId);
        }
      });
    }
  });
  return ids;
};

/**
 * Inverse of `stripEmptyActionsGroups`: re-seeds a direct "actions" group
 * (empty) into the given list and into every loop/branch/else-branch
 * container nested inside it that doesn't already have one -- backend
 * configs never carry a direct actions group for a container whose only
 * content is a nested block, and a previously-stripped save round-trips the
 * same way. The UI model requires that direct group to always exist (it's
 * the write target for `setActionsAt` and the render target for action
 * lists), so this must run right after hydrating any tree coming from the
 * wire (see `cloneAndSetAutomation`).
 *
 * Inserted groups get instanceIds past the current max across the WHOLE
 * tree (group instanceIds and action instanceIds alike, same space
 * `getInstanceId` in mergers/actions/list.ts mints from) so a single pass
 * can insert several groups without colliding. Mutates the containers it
 * touches in place (called once on a freshly cloned tree) but always
 * returns the (possibly same) top-level array so call sites can stay in a
 * pipe.
 */
export const ensureDirectActionsGroups = (
  groups: ActionGroupCandidate[],
): ActionGroupCandidate[] => {
  let nextInstanceId = Math.max(-1, ...collectInstanceIdsDeep(groups)) + 1;

  const seedIfMissing = (list: ActionGroupCandidate[]): void => {
    const hasDirectActionsGroup = list.some(
      (group) => group.config.oneofKind === "actions",
    );
    if (!hasDirectActionsGroup) {
      list.unshift({
        instanceId: nextInstanceId++,
        config: { oneofKind: "actions", actions: { actions: [] } },
      });
    }
  };

  const walk = (list: ActionGroupCandidate[]): void => {
    list.forEach((group) => {
      if (group.config.oneofKind === "loop") {
        walk(group.config.loop.actionGroups);
        seedIfMissing(group.config.loop.actionGroups);
      }
      if (group.config.oneofKind === "branches") {
        group.config.branches.branches.forEach((branch) => {
          walk(branch.actionGroups);
          seedIfMissing(branch.actionGroups);
        });
        if (group.config.branches.elseBranch !== undefined) {
          walk(group.config.branches.elseBranch.actionGroups);
          seedIfMissing(group.config.branches.elseBranch.actionGroups);
        }
      }
    });
  };

  walk(groups);
  seedIfMissing(groups);

  return groups;
};

export const findGroupByInstanceId = (
  groups: ActionGroupCandidate[],
  instanceId: number,
): ActionGroupCandidate | undefined => {
  let found: ActionGroupCandidate | undefined;
  walkActionGroups(groups, (group) => {
    if (found === undefined && group.instanceId === instanceId) {
      found = group;
    }
  });
  return found;
};

export const findTopLevelGroupByActionHash = (
  groups: ActionGroupCandidate[],
  actionHash: string,
): ActionGroupCandidate | undefined =>
  groups.find((group) =>
    collectActionsDeep([group]).some((action) => action.hash === actionHash),
  );

/** True when `group` is a direct "actions" group holding `actionHash`. */
const holdsActionDirectly = (
  group: ActionGroupCandidate,
  actionHash: string,
): boolean =>
  group.config.oneofKind === "actions" &&
  group.config.actions.actions.some((a) => a.hash === actionHash);

/**
 * Locates the NEAREST enclosing container of an action, by hash, anywhere
 * in the tree (root level, or nested inside loops/branches/else-branches to
 * any depth). "Nearest" matters because that's what setActionsAt writes
 * into: an action nested two levels deep (e.g. a loop inside a branch)
 * must be addressed by that innermost loop/branch, not by whichever
 * top-level group happens to contain it -- otherwise edits silently miss
 * their target while the UI's active-item selection still points at the
 * action (see Task 5.5).
 */
export const locateActionByHash = (
  groups: ActionGroupCandidate[],
  actionHash: string,
): GroupLocator | undefined => {
  for (const group of groups) {
    if (holdsActionDirectly(group, actionHash)) {
      return { kind: "root", instanceId: group.instanceId };
    }

    if (group.config.oneofKind === "loop") {
      const { actionGroups } = group.config.loop;
      if (
        actionGroups.some((nested) => holdsActionDirectly(nested, actionHash))
      ) {
        return { kind: "loop", instanceId: group.instanceId };
      }
      const deeper = locateActionByHash(actionGroups, actionHash);
      if (deeper !== undefined) {
        return deeper.kind === "root"
          ? { kind: "loop", instanceId: group.instanceId }
          : deeper;
      }
    }

    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      const allBranches: Array<{
        hash: string;
        actionGroups: ActionGroupCandidate[];
      }> = [
        ...branches.branches.map((b) => ({
          hash: b.hash,
          actionGroups: b.actionGroups,
        })),
        ...(branches.elseBranch !== undefined
          ? [{ hash: "else", actionGroups: branches.elseBranch.actionGroups }]
          : []),
      ];
      for (const branch of allBranches) {
        if (
          branch.actionGroups.some((nested) =>
            holdsActionDirectly(nested, actionHash),
          )
        ) {
          return {
            kind: "branch",
            instanceId: group.instanceId,
            branchHash: branch.hash,
          };
        }
        const deeper = locateActionByHash(branch.actionGroups, actionHash);
        if (deeper !== undefined) {
          return deeper.kind === "root"
            ? {
                kind: "branch",
                instanceId: group.instanceId,
                branchHash: branch.hash,
              }
            : deeper;
        }
      }
    }

    // Repeat isn't a constructible group through the UI and has no
    // dedicated GroupLocator kind -- preserve the pre-existing root
    // fallback (addressed by the repeat group's own instanceId), while
    // still letting any REAL container nested inside it (loop/branches)
    // bubble up as the nearest container.
    if (group.config.oneofKind === "repeat") {
      const actionGroups = group.config.repeat
        .actionGroups as unknown as ActionGroupCandidate[];
      if (
        actionGroups.some((nested) => holdsActionDirectly(nested, actionHash))
      ) {
        return { kind: "root", instanceId: group.instanceId };
      }
      const deeper = locateActionByHash(actionGroups, actionHash);
      if (deeper !== undefined) {
        return deeper.kind === "root"
          ? { kind: "root", instanceId: group.instanceId }
          : deeper;
      }
    }
  }
  return undefined;
};

/**
 * Locates the DIRECT parent container of the group with the given
 * instanceId, anywhere in the tree. Unlike `locateActionByHash` (which
 * locates the container of a leaf ACTION), this locates the container of a
 * GROUP (e.g. a loop or branches block) by its own instanceId -- used to
 * find where to point selection after that group is deleted, since the
 * group itself is gone from the tree by then and must be resolved
 * beforehand.
 *
 * Returns the literal `"root"` (not a `{kind: "root", ...}` locator) when
 * the group sits at the top level of the tree passed in, mirroring the
 * plan's contract; `undefined` when no group with that instanceId is found.
 */
export const locateEnclosingContainer = (
  groups: ActionGroupCandidate[],
  instanceId: number,
): GroupLocator | "root" | undefined => {
  if (groups.some((group) => group.instanceId === instanceId)) {
    return "root";
  }

  for (const group of groups) {
    if (group.config.oneofKind === "loop") {
      const deeper = locateEnclosingContainer(
        group.config.loop.actionGroups,
        instanceId,
      );
      if (deeper !== undefined) {
        return deeper === "root"
          ? { kind: "loop", instanceId: group.instanceId }
          : deeper;
      }
    }

    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      const allBranches: Array<{
        hash: string;
        actionGroups: ActionGroupCandidate[];
      }> = [
        ...branches.branches.map((b) => ({
          hash: b.hash,
          actionGroups: b.actionGroups,
        })),
        ...(branches.elseBranch !== undefined
          ? [{ hash: "else", actionGroups: branches.elseBranch.actionGroups }]
          : []),
      ];
      for (const branch of allBranches) {
        const deeper = locateEnclosingContainer(
          branch.actionGroups,
          instanceId,
        );
        if (deeper !== undefined) {
          return deeper === "root"
            ? {
                kind: "branch",
                instanceId: group.instanceId,
                branchHash: branch.hash,
              }
            : deeper;
        }
      }
    }

    if (group.config.oneofKind === "repeat") {
      const actionGroups = group.config.repeat
        .actionGroups as unknown as ActionGroupCandidate[];
      const deeper = locateEnclosingContainer(actionGroups, instanceId);
      if (deeper !== undefined) {
        return deeper === "root"
          ? { kind: "root", instanceId: group.instanceId }
          : deeper;
      }
    }
  }
  return undefined;
};

/**
 * instanceId of the NEAREST loop enclosing the action, anywhere in the tree,
 * or undefined if the action is not inside any loop. Unlike
 * locateActionByHash (which stops at the nearest container of ANY kind), this
 * climbs PAST branch containers to find the enclosing loop: an action inside
 * a branch that sits inside a loop belongs to that loop's Current-Item scope.
 * When loops are themselves nested, the innermost enclosing loop wins.
 */
export const locateEnclosingLoop = (
  groups: ActionGroupCandidate[],
  actionHash: string,
  enclosingLoopId?: number,
): number | undefined => {
  for (const group of groups) {
    if (holdsActionDirectly(group, actionHash)) {
      return enclosingLoopId;
    }

    if (group.config.oneofKind === "loop") {
      const found = locateEnclosingLoop(
        group.config.loop.actionGroups,
        actionHash,
        group.instanceId,
      );
      if (found !== undefined) return found;
    }

    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      const branchGroups: ActionGroupCandidate[][] = [
        ...branches.branches.map((b) => b.actionGroups),
        ...(branches.elseBranch !== undefined
          ? [branches.elseBranch.actionGroups]
          : []),
      ];
      for (const bg of branchGroups) {
        const found = locateEnclosingLoop(bg, actionHash, enclosingLoopId);
        if (found !== undefined) return found;
      }
    }
  }
  return undefined;
};

/**
 * instanceId of the NEAREST loop enclosing the GROUP with the given
 * instanceId, anywhere in the tree, or undefined if that group is not inside
 * any loop. The group-addressed twin of `locateEnclosingLoop`: it locates a
 * BLOCK (loop/branches/actions group) by its own instanceId instead of an
 * action by hash. Like `locateEnclosingLoop` it climbs PAST branch containers
 * (branches are not loop scopes) to the enclosing loop, and when loops nest
 * the innermost enclosing loop wins. A group asked about itself returns its
 * enclosing loop's scope, not its own id (a top-level loop => undefined) --
 * used to give a branch condition inside a loop its Current-Item scope.
 */
export const locateEnclosingLoopForGroup = (
  groups: ActionGroupCandidate[],
  groupInstanceId: number,
  enclosingLoopId?: number,
): number | undefined => {
  for (const group of groups) {
    if (group.instanceId === groupInstanceId) {
      return enclosingLoopId;
    }

    if (group.config.oneofKind === "loop") {
      const found = locateEnclosingLoopForGroup(
        group.config.loop.actionGroups,
        groupInstanceId,
        group.instanceId,
      );
      if (found !== undefined) return found;
    }

    if (group.config.oneofKind === "branches") {
      const { branches } = group.config;
      const branchGroups: ActionGroupCandidate[][] = [
        ...branches.branches.map((b) => b.actionGroups),
        ...(branches.elseBranch !== undefined
          ? [branches.elseBranch.actionGroups]
          : []),
      ];
      for (const bg of branchGroups) {
        const found = locateEnclosingLoopForGroup(
          bg,
          groupInstanceId,
          enclosingLoopId,
        );
        if (found !== undefined) return found;
      }
    }
  }
  return undefined;
};
